/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SharedModule
 */

import { isObject } from 'underscore';

import {
    IIpAddrPrefix,
    IpAddrType,
} from 'generated-types';

import {
    ipv6Prefix as ipv6PrefixPrefix,
    subnetWG as ipv4PrefixRegex,
} from './regex.utils';

import { getIpAddr } from './ip-parser.utils';

/**
 * Returns readable IP-Subnet string from IpAddrPrefix type object. If any of the fields are not
 * present, returns undefined.
 */
export function getSubnetString(subnetObj: IIpAddrPrefix): string | undefined {
    if (!isObject(subnetObj)) {
        return;
    }

    const {
        ip_addr: { addr } = {},
        mask,
    } = subnetObj;

    if (addr === undefined || mask === undefined) {
        return;
    }

    return `${addr}/${mask}`;
}

/**
 * Parses IP-Subnet string and returns IpAddrPrefix  type object.
 * throws error for invalid cases.
 */
export function parseIpPrefix(ipPrefixString: string): IIpAddrPrefix {
    if (ipv4PrefixRegex.test(ipPrefixString)) {
        const [, ipAddr, mask] = ipv4PrefixRegex.exec(ipPrefixString);

        return {
            ip_addr: getIpAddr(ipAddr),
            mask: +mask,
        };
    }

    if (ipv6PrefixPrefix.test(ipPrefixString)) {
        const ipv6PrefixChunks = ipv6PrefixPrefix.exec(ipPrefixString);
        const ipAddr = ipv6PrefixChunks[1];
        const mask = ipv6PrefixChunks[ipv6PrefixChunks.length - 1];

        return {
            ip_addr: getIpAddr(ipAddr, IpAddrType.V6),
            mask: +mask,
        };
    }

    throw new Error(`Invalid IP Prefix: ${ipPrefixString}`);
}

/**
 * Returns IpAddrPrefix object from IP-Subnet string.
 * returns null for invalid cases.
 */
export function parseIpPrefixString(ipPrefixString: string): IIpAddrPrefix | null {
    try {
        ipPrefixString = ipPrefixString.trim();

        return parseIpPrefix(ipPrefixString);
    } catch (e) {
        return null;
    }
}

/**
 * Returns IP-subnet string from IpAddrPrefix object. Will always return a string even if the prefix
 * object is invalid, so it's preferred over getSubnetString for anything view-related like dropdown
 * options.
 */
export function formatIpAddrPrefix(ipAddrPrefix: IIpAddrPrefix): string {
    return getSubnetString(ipAddrPrefix) || '';
}
